// TapGame.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import targetImage from '../assets/images/tap.png';
import damagedImage from '../assets/images/tap.png';
import outOfCreditsImage from '../assets/images/tap.png';
import GamesTopBar from './GamesTopBar';
import { useAppConfig } from '../ConfigContext';
import OpenBonusModal from './OpenBonusModal';
import LoadingSpinner from './LoadingSpinner';
import '../index.css';

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
const DEFAULT_TOTAL_LEVELS = 30;

const USE_BATCH_TAPS = true;
const BATCH_FLUSH_MS = 500;
const BATCH_MAX_COUNT = 50;

const ENABLE_REFILL_MODAL = true;

const TapGame = ({
  user,
  setShowSeasonChange,
  setUser,
  onNavigate,
  whitelistEnabled = false,
  onWhitelistBlocked,
  isGated,
  gate,
  tournamentEnabled = false,
  tournamentBadgeOn = false,
  tournamentStartDate,
  tournamentType,
  tournamentEndDate,
  activeTournamentId,
  onOpenShop,
  soundEnabled,
  sfxEnabled,
  sfxVolume,
}) => {
  const donotshow = 1;
  const airdrop = !!user?.hasUnclaimedAirdrop;
  const [showDamage, setShowDamage] = useState(false);
  const [tapEffects, setTapEffects] = useState([]);
  const [booster, setBooster] = useState(1);
  const [boosterTimeLeft, setBoosterTimeLeft] = useState(0);
  const [showBoosterModal, setShowBoosterModal] = useState(false);
  const [pendingBooster, setPendingBooster] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [userLoaded, setUserLoaded] = useState(false);
  const [runesAwarded, setRunesAwarded] = useState(0);
  const [rechargeEndsAt, setRechargeEndsAt] = useState(null); // ms epoch when credits recharge ends
  const [tick, setTick] = useState(0);
  const endServerMsRef = useRef(null);

  const tapQueueRef = useRef({ count: 0, booster: 1 });
  const flushTimerRef = useRef(null);
  const flushingRef = useRef(false);

  const [tournamentStats, setTournamentStats] = useState(null);
  const [tournamentLoading, setTournamentLoading] = useState(false);
  const [tournamentError, setTournamentError] = useState('');
  const [refillLoading, setRefillLoading] = useState(null); // 'video' | 'stars' | null
  const [refillError, setRefillError] = useState('');
  const [showRefillModal, setShowRefillModal] = useState(false);
  const tapRefillAdControllerRef = useRef(null);
  const [openBonusAvailableAt, setOpenBonusAvailableAt] = useState(null); // null = available, ISO string = cooldown until
  const [showOpenBonusModal, setShowOpenBonusModal] = useState(false);

  const RECHARGE_CACHE_KEY = (uid) => `tap:recharge:${uid ?? 'anon'}`;
  const BOOSTER_COOLDOWN_KEY = (uid) => `tap:boosterCooldown:${uid ?? 'anon'}`;
  const TAP_EVENT_COOLDOWN_KEY = (uid) => `tap:eventCooldown:${uid ?? 'anon'}`;

  // Safe localStorage check (covers SSR/private-mode)
  const hasLS = () => {
    try {
      return typeof window !== 'undefined' && !!window.localStorage;
    } catch {
      return false;
    }
  };

  // pending deltas not yet confirmed by server
  const pendingRef = useRef({
    taps: 0, // how many taps spent locally but not flushed
    inc: 0, // total booster-weighted progress (xp/sparks/levelTaps) not flushed
    booster: 1, // booster used for the pending batch
    lockedDone: false, // set true when server says completed
  });

  const inflightRef = useRef({
    taps: 0,
    inc: 0,
    booster: 1,
    seq: 0,
  });

  const [, forcePendingRerender] = useState(0);
  const bumpPending = () => forcePendingRerender((x) => x + 1);

  // Use pendingRef to render "effective" values (server truth + local unflushed taps)
  const pending = pendingRef.current;
  const inflight = inflightRef.current;

  const effectiveCredits = useMemo(() => {
    const base = Number(user?.credits ?? 0);
    const spent = (pending?.taps || 0) + (inflight?.taps || 0);
    return Math.max(0, base - spent);
  }, [user?.credits, pending?.taps, inflight?.taps]);

  const effectiveLevelTaps = useMemo(() => {
    const base = Number(user?.levelTaps ?? 0);
    const add = (pending?.inc || 0) + (inflight?.inc || 0);
    return base + add;
  }, [user?.levelTaps, pending?.inc, inflight?.inc]);

  const effectiveXp = useMemo(() => {
    const base = Number(user?.xp ?? 0);
    const add = (pending?.inc || 0) + (inflight?.inc || 0);
    return base + add;
  }, [user?.xp, pending?.inc, inflight?.inc]);

  const effectiveSparks = useMemo(() => {
    const base = Number(user?.sparks ?? 0);
    const add = (pending?.inc || 0) + (inflight?.inc || 0);
    return base + add;
  }, [user?.sparks, pending?.inc, inflight?.inc]);

  // SOUND

  const tapAudioRef = useRef(null);

  // create + preload once
  useEffect(() => {
    const tap = new Audio('/tap.mp3');
    tap.preload = 'auto';
    tapAudioRef.current = tap;

    try {
      tap.load();
    } catch {}
    return () => {
      try {
        tap.pause();
      } catch {}
    };
  }, []);

  // apply volume immediately
  useEffect(() => {
    const tap = tapAudioRef.current;
    if (!tap) return;
    tap.volume = clamp(Number(sfxVolume ?? 0.9), 0, 1);
  }, [sfxVolume]);

  const playTapSfx = () => {
    if (!sfxEnabled) return;

    const tap = tapAudioRef.current;
    if (!tap) return;

    tap.volume = clamp(Number(sfxVolume ?? 0.9), 0, 1);

    try {
      tap.pause();
      tap.currentTime = 0;
      const p = tap.play();
      if (p?.catch) p.catch(() => {});
    } catch {}
  };

  // Animated list item for each loot line
  const LootRow = ({ icon, label, amount, iconSize = 24, animate = true }) => {
    if (!animate) {
      return (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `${iconSize}px 1fr auto`,
            alignItems: 'center',
            gap: 10,
          }}
        >
          <div
            style={{
              width: iconSize,
              height: iconSize,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            {React.isValidElement(icon) ? (
              React.cloneElement(icon, {
                style: {
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  ...(icon.props?.style || {}),
                },
              })
            ) : (
              <span style={{ fontSize: iconSize, lineHeight: 1 }}>{icon}</span>
            )}
          </div>

          <div style={{ color: '#eee' }}>{label}</div>

          <div
            style={{
              color: '#161616',
              background: 'linear-gradient(90deg,#FFD54F,#FF6EC7)',
              padding: '2px 10px',
              borderRadius: 999,
              fontWeight: 800,
            }}
          >
            +{amount}
          </div>
        </div>
      );
    }

    // 👉 ANIMATED VERSION (used everywhere else)
    return (
      <motion.div
        variants={{
          hidden: { opacity: 0, y: 8, scale: 0.98 },
          show: { opacity: 1, y: 0, scale: 1 },
        }}
        initial="hidden"
        animate="show"
        style={{
          display: 'grid',
          gridTemplateColumns: `${iconSize}px 1fr auto`,
          alignItems: 'center',
          gap: 10,
        }}
      >
        <motion.div
          initial={{ rotate: -8 }}
          animate={{ rotate: 0 }}
          transition={{ type: 'spring', stiffness: 260, damping: 18 }}
          style={{
            width: iconSize,
            height: iconSize,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {React.isValidElement(icon) ? (
            React.cloneElement(icon, {
              style: {
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                ...(icon.props?.style || {}),
              },
            })
          ) : (
            <span style={{ fontSize: iconSize, lineHeight: 1 }}>{icon}</span>
          )}
        </motion.div>

        <div style={{ color: '#eee' }}>{label}</div>

        <motion.div
          initial={{ scale: 0.9 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 300, damping: 16 }}
          style={{
            color: '#161616',
            background: 'linear-gradient(90deg,#FFD54F,#FF6EC7)',
            padding: '2px 10px',
            borderRadius: 999,
            fontWeight: 800,
          }}
        >
          +{amount}
        </motion.div>
      </motion.div>
    );
  };

  const formatTournamentCopy = () => {
    if (!tournamentEnabled) return null;
    if (tournamentError) return tournamentError;
    if (tournamentLoading || !tournamentStats) return 'Loading live tournament stats...';

    const good = tournamentStats?.sides?.good || { wins: 0, losses: 0 };
    const bad = tournamentStats?.sides?.bad || { wins: 0, losses: 0 };

    const goodWins = good.wins ?? 0;
    const badWins = bad.wins ?? 0;

    const total = goodWins + badWins || 0;

    const startLabel = (() => {
      try {
        const d = new Date(tournamentStats.from || tournamentStartDate);
        return d.toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
        });
      } catch {
        return tournamentStats.from || tournamentStartDate;
      }
    })();

    if (total === 0) {
      return `Tournament is live since ${startLabel}. No recorded wins yet – be the first one to tilt the balance!`;
    }

    if (goodWins === badWins) {
      return `Since ${startLabel}, the war is perfectly balanced: Sun Pharaohs and Moon Anubis are tied at ${goodWins} victories each. Every fight now matters.`;
    }

    const leadingSide = goodWins > badWins ? 'good' : 'bad';
    const diff = Math.abs(goodWins - badWins);

    if (leadingSide === 'good') {
      return `Since ${startLabel}, Sun Pharaohs are shining brighter: ${goodWins} wins vs ${badWins} for Moon Anubis (lead of ${diff}).`;
    }

    return `Since ${startLabel}, Moon Anubis rule the night: ${badWins} wins vs ${goodWins} for Sun Pharaohs (lead of ${diff}).`;
  };

  // Floating sparkle emojis (lightweight)
  const SparkleEmojis = () => {
    const items = ['✨', '💎', '🌟', '🪄', '💫', '🎉'];
    return (
      <div aria-hidden style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        {items.map((e, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 0.6, y: -10 }}
            transition={{
              repeat: Infinity,
              repeatType: 'mirror',
              duration: 2 + (i % 3),
              delay: i * 0.2,
            }}
            style={{
              position: 'absolute',
              top: `${10 + ((i * 12) % 70)}%`,
              left: `${8 + ((i * 17) % 84)}%`,
              fontSize: 14 + (i % 3) * 4,
            }}
          >
            {e}
          </motion.div>
        ))}
      </div>
    );
  };


  const getPrizeIconSrc = (item) => {
    const basis = (item?.itemType || item?.type || item?.name || '').toLowerCase();

    if (basis.includes('spark')) return '/sparks.png';
    if (basis.includes('rune')) return '/runes.png';
    if (basis.includes('seasonal_accessory')) return '/fragment.png';
    if (basis.includes('booster')) return '/energy.png';
    if (basis.includes('card')) return '/common.png';
    if (basis.includes('ability')) return '/fightlog.png';
    if (basis.includes('evolve')) return '/evolveicon.png';
    if (basis.includes('spins')) return '/fortuneicon.png';
    return '/chest.png';
  };

  function readRechargeFromAnyCache(userId) {
    const precise = readRechargeFromCache(userId);
    if (precise) return precise;
    const fallback = readRechargeFromCache(null); // 'anon'
    if (!fallback) return null;
    // migrate once
    if (userId)
      saveRechargeToCache({
        userId,
        endServerMs: fallback.endServerMs,
        sampledServerMs: fallback.sampledServerMs,
      });
    clearRechargeCache(null);
    return fallback;
  }

  function getEstimatedServerNowFromCache(userId) {
    const cached = readRechargeFromAnyCache(userId);
    if (!cached?.sampledClientMs) return null;
    // If we don't have a server clock, fall back to client time as the base.
    const base = Number.isFinite(cached?.sampledServerMs)
      ? cached.sampledServerMs
      : cached.sampledClientMs;
    if (!Number.isFinite(base)) return null;
    const delta = Date.now() - cached.sampledClientMs;
    return base + Math.max(0, delta);
  }

  function saveRechargeToCache({ userId, endServerMs, sampledServerMs }) {
    if (!hasLS()) return;
    try {
      localStorage.setItem(
        RECHARGE_CACHE_KEY(userId),
        JSON.stringify({
          endServerMs, // absolute server epoch ms when recharge finishes
          sampledServerMs, // server "now" when we saved
          sampledClientMs: Date.now(), // client "now" for drift estimation before the next server ping
          userId,
        }),
      );
    } catch {}
  }

  function readRechargeFromCache(userId) {
    if (!hasLS()) return null;
    try {
      const raw = localStorage.getItem(RECHARGE_CACHE_KEY(userId));
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  function clearRechargeCache(userId) {
    if (!hasLS()) return;
    try {
      localStorage.removeItem(RECHARGE_CACHE_KEY(userId));
    } catch {}
  }

  const gated = typeof isGated === 'function' ? !!isGated() : !!whitelistEnabled;
  const blockIfGated = (actionName) => {
    if (gated) {
      onWhitelistBlocked?.(actionName);
      return true;
    }
    return false;
  };

  // NEW
  const [configReady, setConfigReady] = useState(false);
  const [statusReady, setStatusReady] = useState(false);
  const bootLocked = !(configReady && statusReady);
  const uiLocked = bootLocked;
  const [boosterCooldownMs, setBoosterCooldownMs] = useState(0);
  const [tapEventCooldownMs, setTapEventCooldownMs] = useState(0);

  // Completion prize modal
  const [showWinModal, setShowWinModal] = useState(false);
  const [winPrize, setWinPrize] = useState(0);
  const [winItems, setWinItems] = useState([]);

  const isDirectBalanceReward = (item) => {
    const basis = String(item?.itemType || item?.type || item?.name || '').toLowerCase();
    return basis.includes('spark') || basis.includes('rune');
  };

  const winItemsSummary = useMemo(() => {
    const items = Array.isArray(winItems) ? winItems : [];

    const hasBalanceRewards = winPrize > 0 || items.some((item) => isDirectBalanceReward(item));
    const hasInventoryRewards = items.some((item) => !isDirectBalanceReward(item));

    if (hasBalanceRewards && hasInventoryRewards) {
      return 'Some rewards were credited to your balance, and the rest were added to your inventory.';
    }

    if (hasBalanceRewards) {
      return 'Your rewards were credited to your balance.';
    }

    if (hasInventoryRewards) {
      return 'Your rewards were added to your inventory.';
    }

    return '';
  }, [winPrize, winItems]);

  // Config from Admin CMS
  const [levels, setLevels] = useState([]); // [{level, requiredTaps, barCapacity, cooldownSeconds}]
  const [prizeAtCompletion, setPrizeAtCompletion] = useState(0);

  const [gpLevels, setGpLevels] = useState([]);
  const [gpItemsById, setGpItemsById] = useState({});
  const [hasGamepass, setHasGamepass] = useState(!!(user?.gamepassActive ?? user?.gamepass_active));

  // Fallback context
  const { taps, booster: boosterCfg } = useAppConfig();
  const { whitelistEnabled: wlEnabledFromCtx, newUsers } = useAppConfig();

  const haveNewUserRewards = useMemo(() => {
    if (!newUsers) return false;
    const anyCurrency =
      (newUsers.bonusSparks | 0) > 0 ||
      (newUsers.bonusRunes | 0) > 0 ||
      (newUsers.bonusCredits | 0) > 0;
    const anyCard = newUsers.starterRarity && newUsers.starterRarity !== 'None';
    return anyCurrency || anyCard;
  }, [newUsers]);

  useEffect(() => {
    let cancelled = false;

    const loadGamepass = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/gamepass`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            Accept: 'application/json',
          },
        });

        if (!res.ok) {
          // optional: log but don't break TapGame if gamepass fails
          console.warn('TapGame: failed to load /api/gamepass');
          return;
        }

        const data = await res.json();
        if (cancelled) return;

        setGpLevels(Array.isArray(data.levels) ? data.levels : []);
        setGpItemsById(data.itemsById || {});
        setHasGamepass(!!data.hasGamepass);
      } catch (e) {
        if (!cancelled) {
          console.warn('TapGame: /api/gamepass error', e);
        }
      }
    };

    loadGamepass();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    if (boosterCooldownMs <= 0) return;
    const id = setInterval(() => {
      setBoosterCooldownMs((prev) => {
        const next = prev - 1000;
        if (next <= 0) {
          // Clear when finished
          setTimeout(() => {
            try {
              if (hasLS()) {
                localStorage.removeItem(BOOSTER_COOLDOWN_KEY(user?.id));
              }
            } catch {}
          }, 0);
          return 0;
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [boosterCooldownMs, user?.id]);

  // Tap event cooldown (3h) tick-down
  useEffect(() => {
    if (tapEventCooldownMs <= 0) return;
    const id = setInterval(() => {
      setTapEventCooldownMs((prev) => {
        const next = prev - 1000;
        if (next <= 0) {
          setTimeout(() => {
            try {
              if (hasLS()) {
                localStorage.removeItem(TAP_EVENT_COOLDOWN_KEY(user?.id));
              }
            } catch {}
          }, 0);
          return 0;
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [tapEventCooldownMs, user?.id]);

  // --- CONFIG LOADER (Admin CMS) ---
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/config`, {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) throw new Error('config fetch failed');
        const cfg = await res.json();
        const rows = cfg?.levels;

        if (Array.isArray(rows) && rows.length > 0) {
          const norm = rows
            .map((r, i) => ({
              level: Number(r.level ?? i + 1),
              requiredTaps: Number(r.requiredTaps ?? 0),
              barCapacity: Number(r.barCapacity ?? 0),
              cooldownSeconds: Number(r.cooldownSeconds ?? 0),
            }))
            .sort((a, b) => a.level - b.level);
          setLevels(norm);
          setPrizeAtCompletion(Number(cfg?.prizeAtCompletion ?? 0) || 0);
          setConfigReady(true);
        } else {
          // If you prefer to keep the game locked until proper config arrives, leave this false.
          // Otherwise, you could decide to unlock with defaults:
          // setConfigReady(true);
        }
      } catch (e) {
        console.warn('Failed to load /api/config:', e);
        // Choose your policy. To be conservative (no over-tap), stay locked:
        // setConfigReady(false);
        // Or to allow play with defaults, uncomment:
        // setConfigReady(true);
      }
    })();
  }, []);

  
  // --- INITIAL STATUS SYNC (always, not only when credits==0) ---
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/status`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        setUser((prev) => ({
          ...prev,
          credits: typeof data.credits === 'number' ? data.credits : prev?.credits,
          level: typeof data.level === 'number' ? data.level : prev?.level ?? 1,
          levelTaps: typeof data.levelTaps === 'number' ? data.levelTaps : prev?.levelTaps ?? 0,
          tapDepletionCount:
            typeof data.tapDepletionCount === 'number'
              ? data.tapDepletionCount
              : prev?.tapDepletionCount,
        }));

        // Seed booster UI from server (for BOTH inventory-claimed and purchased boosters)
        const boosterMult = Number(data.boosterMultiplier) || 1;
        const boosterSecLeft = Number(data.boosterTimeLeft) || 0;

        if (boosterMult > 1 && boosterSecLeft > 0) {
          setBooster(boosterMult);
          setBoosterTimeLeft(boosterSecLeft);
        } else {
          setBooster(1);
          setBoosterTimeLeft(0);
        }

        setOpenBonusAvailableAt(
          data.openBonusAvailableAt != null ? data.openBonusAvailableAt : null,
        );
      } catch (err) {
        console.error('Initial status sync failed:', err);
      } finally {
        setStatusReady(true);
      }
    })();
  }, [setUser]);

  // keep a one-time loaded flag
  useEffect(() => {
    if (user && !userLoaded) setUserLoaded(true);
  }, [user, userLoaded]);

  // If we don't have user.side, try to hydrate from /api/me (best effort)
  useEffect(() => {
    if (user?.side == null) {
      (async () => {
        try {
          const meRes = await fetch(`${process.env.REACT_APP_API_URL}/api/me`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          });
          if (meRes.ok) {
            const me = await meRes.json();
            setUser((prev) => ({
              ...prev,
              side: me?.side ?? prev?.side,
              lastRecharge: me?.lastRecharge ?? prev?.lastRecharge,
              tapi: me?.tapi
            }));
          }
        } catch (_) {}
      })();
    }
  }, [user?.side, setUser]);

  useEffect(() => {
    if (!hasLS() || !user?.id) return;
    try {
      const raw = localStorage.getItem(BOOSTER_COOLDOWN_KEY(user.id));
      if (!raw) return;
      const until = Number(raw);
      if (!Number.isFinite(until)) return;
      const remaining = until - Date.now();
      if (remaining > 0) {
        setBoosterCooldownMs(remaining);
      } else {
        localStorage.removeItem(BOOSTER_COOLDOWN_KEY(user.id));
      }
    } catch {}
  }, [user?.id]);

  // Hydrate tap event cooldown from localStorage (best-effort UX only; server is source of truth)
  useEffect(() => {
    if (!hasLS() || !user?.id) return;
    try {
      const raw = localStorage.getItem(TAP_EVENT_COOLDOWN_KEY(user.id));
      if (!raw) return;
      const until = Number(raw);
      if (!Number.isFinite(until)) return;
      const remaining = until - Date.now();
      if (remaining > 0) {
        setTapEventCooldownMs(remaining);
      } else {
        localStorage.removeItem(TAP_EVENT_COOLDOWN_KEY(user.id));
      }
    } catch {}
  }, [user?.id]);

  const totalLevels =
    (levels?.length && Math.min(levels.length, DEFAULT_TOTAL_LEVELS)) || DEFAULT_TOTAL_LEVELS;

  // --- DERIVED PER-LEVEL SETTINGS ---
  // IMPORTANT: do NOT fallback to tapDepletionCount anymore
  const levelIndex = useMemo(() => {
    const maxIndex = Math.max(0, totalLevels - 1);
    return clamp((Number(user?.level) || 1) - 1, 0, maxIndex);
  }, [user?.level, totalLevels]);

  const currentLevel = levels[levelIndex] || {
    level: levelIndex + 1,
    requiredTaps: 0,
    barCapacity: Number(taps?.total ?? 100),
    cooldownSeconds: Number(taps?.countdownTime ?? 60),
  };

  // Level-based capacity/cooldown
  const CREDIT_RECHARGE_MS = currentLevel.cooldownSeconds * 1000;
  const MAX_CREDITS = currentLevel.barCapacity;

  //useEffect(() => {
  //  if (!configReady || !statusReady) return;
  //  if (!user?.id) return;
    // Show only if whitelist is OFF and there is something configured for new users
  //  if (wlEnabledFromCtx !== false) return;
  //  if (!haveNewUserRewards) return;
    // Prevent repeats (per-user)
   // try {
    //  const k = WELCOME_KEY(user.id);
    //  if (localStorage.getItem(k)) return;
    //  setShowWelcomeModal(true);
    //  localStorage.setItem(k, '1');
    //} catch {}
  //}, [configReady, statusReady, user?.id, wlEnabledFromCtx, haveNewUserRewards]);

  // Initialize cooldown from /me (real server time)
  //useEffect(() => {
  //  if (Number(user?.credits ?? 0) !== 0) return;        // only when out of credits
  //  if (!user?.lastRecharge) return;                     // needs timestamp from /me
  //  const last = new Date(user.lastRecharge).getTime();
  //  setRechargeEndsAt(last + CREDIT_RECHARGE_MS);
  //}, [user?.credits, user?.lastRecharge, CREDIT_RECHARGE_MS]);

  // --- BOOSTER TIERS ---
  const boosterTiers = useMemo(() => {
    const tiers = Array.isArray(boosterCfg?.tiers) ? boosterCfg.tiers : [];
    return tiers
      .map((t) => ({ increment: Number(t.increment), price: Number(t.price) }))
      .filter((t) => Number.isFinite(t.increment) && t.increment > 1 && Number.isFinite(t.price))
      .sort((a, b) => a.increment - b.increment);
  }, [boosterCfg]);

  const getBoosterPrice = (multiplier) =>
    boosterTiers.find((t) => t.increment === Number(multiplier))?.price ?? Infinity;

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== RECHARGE_CACHE_KEY(user?.id) || e.newValue === null) return;
      const cached = readRechargeFromCache(user?.id);
      if (!cached) return stopCountdown();
      const delta = Date.now() - (cached.sampledClientMs ?? Date.now());
      const estServerNow = (cached.sampledServerMs ?? 0) + Math.max(0, delta);
      const remaining = Math.max(0, (cached.endServerMs ?? 0) - estServerNow);
      if (remaining > 0) {
        startCountdown(estServerNow, remaining, { persist: true });
      } else {
        stopCountdown();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [user?.id]);

  // run once on mount
  useEffect(() => {
    const cached = readRechargeFromAnyCache(user?.id);
    if (!cached?.endServerMs || !cached?.sampledServerMs || !cached?.sampledClientMs) return;

    // Estimate current *server* time using the previous server stamp + client elapsed.
    const clientDelta = Date.now() - cached.sampledClientMs;
    const estServerNow = cached.sampledServerMs + Math.max(0, clientDelta);

    const remaining = Math.max(0, cached.endServerMs - estServerNow);
    if (remaining > 0) {
      startCountdown(estServerNow, remaining, { persist: true });
    } else {
      clearRechargeCache(user?.id);
    }
  }, [user?.id]);

  // inside component, after state declarations
  const startCountdown = (serverNowMs, remainingMs, { persist = true } = {}) => {
    // Treat an anchor as "server-trustworthy" only if we actually got it from server/cache.
    const isServerAnchored = persist && Number.isFinite(Number(serverNowMs));
    const safeRemaining = Math.max(0, Number(remainingMs) || 0);
    const endMs = (Number(serverNowMs) || Date.now()) + safeRemaining; // <- single name

    if (safeRemaining <= 0) {
      stopCountdown();
      return;
    }

    if (endServerMsRef.current != null && endMs > endServerMsRef.current && !isServerAnchored) {
      // We only allow shortening or equal (dedupe below), unless it's server-anchored.
      return;
    }

    // 2) De-dupe tiny jitter.
    if (endServerMsRef.current != null && Math.abs(endMs - endServerMsRef.current) < 800) {
      return;
    }

    setRechargeEndsAt(endMs);
    setInitialRemainingMs(safeRemaining);
    setPerfStart(performance.now());
    endServerMsRef.current = endMs;

    saveRechargeToCache({
      userId: user?.id,
      endServerMs: endMs,
      sampledServerMs: isServerAnchored ? Number(serverNowMs) : Date.now(), // soft anchor uses client "now" as base
    });
  };

  const stopCountdown = () => {
    // FIX: remove recursion, just clear state + cache
    setRechargeEndsAt(null);
    setInitialRemainingMs(null);
    setPerfStart(null);
    endServerMsRef.current = null;
    clearRechargeCache(user?.id);
  };

  // Booster timer
  useEffect(() => {
    if (booster <= 1 || boosterTimeLeft <= 0) return;
    const interval = setInterval(() => {
      setBoosterTimeLeft((time) => {
        if (time <= 1) {
          setBooster(1);
          clearInterval(interval);
          return 0;
        }
        return time - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [booster, boosterTimeLeft]);

  const getServerNowMs = (res, data) => {
    const tryParse = (v) => {
      const t = Date.parse(v || '');
      return Number.isFinite(t) ? t : null;
    };
    if (data?.serverNow) {
      const t = tryParse(data.serverNow);
      if (t != null) return t;
    }
    const hdrDate = res?.headers?.get?.('Date');
    const t2 = tryParse(hdrDate);
    return t2; // null if neither parsed
  };

  const [initialRemainingMs, setInitialRemainingMs] = useState(null);
  const [perfStart, setPerfStart] = useState(null);

  // When credits hit 0, figure out when we can tap again (server truth)
  useEffect(() => {
    if (effectiveCredits === 0 && configReady) {
      //if (user?.lastRecharge) return;
      const fetchInitialRecharge = async () => {
        try {
          const res = await fetch(`${process.env.REACT_APP_API_URL}/api/status`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          });
          const data = await res.json();

          setUser((prev) => ({
            ...prev,
            credits: data.credits,
            level: typeof data.level === 'number' ? data.level : prev?.level,
            levelTaps: typeof data.levelTaps === 'number' ? data.levelTaps : prev?.levelTaps,
            tapDepletionCount:
              typeof data.tapDepletionCount === 'number'
                ? data.tapDepletionCount
                : prev?.tapDepletionCount,
            lastRecharge: data.lastRecharge ?? prev?.lastRecharge,
          }));

          //if (data.lastRecharge) {
          //  const last = new Date(data.lastRecharge).getTime();
          //  setRechargeEndsAt(last + CREDIT_RECHARGE_MS);
          //} else {
          //  setRechargeEndsAt(null);
          //}

          const serverNowMsRaw = getServerNowMs(res, data);

          //console.log('[TAPGAME time]', {
          // where: 'initialRecharge',
          // serverNowMsRaw,
          // dateHdr: res.headers?.get?.('Date'),
          // jsonServerNow: data?.serverNow,
          //});

          const estFromCache = getEstimatedServerNowFromCache(user?.id);
          const estServerNow = serverNowMsRaw ?? estFromCache; // prefer live server time, fallback to cached estimate
          let lastMs = data.lastRecharge ? Date.parse(data.lastRecharge) : null;

          if (data.credits === 0) {
            if (lastMs == null || estServerNow == null) {
              const cachedNow = getEstimatedServerNowFromCache(user?.id);
              if (
                cachedNow != null &&
                endServerMsRef.current == null &&
                initialRemainingMs == null
              ) {
                if (configReady)
                  startCountdown(cachedNow, CREDIT_RECHARGE_MS, {
                    persist: true,
                  });
              } else if (endServerMsRef.current == null && initialRemainingMs == null) {
                // Soft local countdown until we can read server time (not persisted).
                if (configReady)
                  startCountdown(Date.now(), CREDIT_RECHARGE_MS, {
                    persist: false,
                  });
              }

              return;
            }
            const elapsed = Math.max(0, estServerNow - lastMs);
            const remaining = Math.max(0, CREDIT_RECHARGE_MS - elapsed);
            if (Number.isFinite(remaining) && remaining > 0) {
              startCountdown(estServerNow, remaining, { persist: true });
            } else {
              stopCountdown();
            }
          } else {
            stopCountdown();
          }
        } catch (err) {
          console.error('Failed to fetch recharge status:', err);
        }
      };
      fetchInitialRecharge();
    }
  }, [
    user?.credits,
    user?.lastRecharge,
    currentLevel.cooldownSeconds,
    CREDIT_RECHARGE_MS,
    configReady,
    setUser,
  ]);

  const fetchRechargeStatus = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await res.json();
      if (data.credits > 0) {
        setUser((prev) => ({
          ...prev,
          credits: data.credits,
          level: typeof data.level === 'number' ? data.level : prev?.level,
          levelTaps: typeof data.levelTaps === 'number' ? data.levelTaps : prev?.levelTaps,
        }));
        stopCountdown();
      } else {
        const serverNowMsRaw = getServerNowMs(res, data);

        //console.log('[TAPGAME time]', {
        //where: 'initialRecharge',
        //serverNowMsRaw,
        //dateHdr: res.headers?.get?.('Date'),
        // jsonServerNow: data?.serverNow,
        //});

        const estFromCache = getEstimatedServerNowFromCache(user?.id);
        const estServerNow = serverNowMsRaw ?? estFromCache;
        if (estServerNow == null) {
          const cachedNow = getEstimatedServerNowFromCache(user?.id);
          if (cachedNow != null && endServerMsRef.current == null && initialRemainingMs == null) {
            if (configReady) startCountdown(cachedNow, CREDIT_RECHARGE_MS, { persist: true });
          } else if (endServerMsRef.current == null && initialRemainingMs == null) {
            // Soft local countdown until server time arrives (not persisted, shown once).
            if (configReady)
              startCountdown(Date.now(), CREDIT_RECHARGE_MS, {
                persist: false,
              });
          }
          return;
        }
        const lastMs = data.lastRecharge ? Date.parse(data.lastRecharge) : null;
        if (lastMs == null) {
          // No server anchor: use cached end if present rather than restarting a full timer.
          const cached = readRechargeFromAnyCache(user?.id);
          const remainingFromCache =
            cached?.endServerMs && estServerNow != null
              ? Math.max(0, cached.endServerMs - estServerNow)
              : null;
          if (Number.isFinite(remainingFromCache) && remainingFromCache > 0) {
            startCountdown(estServerNow, remainingFromCache, { persist: true });
          } else {
            // keep “Recharging” state; the 2s poll will pick up refill
            stopCountdown();
          }
          return;
        }
        const elapsed = Math.max(0, estServerNow - lastMs);
        const remaining = Math.max(0, CREDIT_RECHARGE_MS - elapsed);
        if (Number.isFinite(remaining) && remaining > 0) {
          startCountdown(estServerNow, remaining, { persist: true });
        } else {
          stopCountdown();
        }
      }
    } catch (err) {
      console.error('Failed to check status', err);
    }
  };

  // poll server while empty and we have an end time
  useEffect(() => {
    if (!configReady || effectiveCredits !== 0) return;
    const id = setInterval(fetchRechargeStatus, 2000);
    return () => clearInterval(id);
  }, [effectiveCredits, configReady]);

  // 1s tick to refresh the "Reload in" view using real time
  useEffect(() => {
    if (initialRemainingMs == null || perfStart == null) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [initialRemainingMs, perfStart]);

  // ====== Completed-game lock & UI (ONLY ONCE) ======
  const onFinalLevel = Number(user?.level ?? 1) >= totalLevels;
  const allLevelsDone =
    onFinalLevel &&
    currentLevel.requiredTaps > 0 &&
    Number(user?.levelTaps ?? 0) >= currentLevel.requiredTaps;

  useEffect(() => {
    if (allLevelsDone) setRechargeEndsAt(null);
  }, [allLevelsDone]);

  // Show Open Bonus modal on app start if bonus is available, after 5s delay
  useEffect(() => {
    if (!statusReady || !user?.id) return;
    if (allLevelsDone) return;
    const available = openBonusAvailableAt == null;
   if (!available) return;
    const t = setTimeout(() => setShowOpenBonusModal(true), 5000);
    return () => clearTimeout(t);
  }, [statusReady, user?.id, openBonusAvailableAt, allLevelsDone]);

  const tapsLeftDisplay = allLevelsDone ? 0 : effectiveCredits;
  const canTap = !bootLocked && !allLevelsDone && effectiveCredits > 0 && !gated;
  const outOfTaps = !allLevelsDone && tapsLeftDisplay <= 0;

  const boosterActive = booster > 1 && boosterTimeLeft > 0 && !allLevelsDone;
  const boosterOnCooldown = boosterCooldownMs > 0;

  const boosterButtonDisabled = uiLocked || allLevelsDone || boosterActive;

  // Tap event popup state
  const [showTapEventModal, setShowTapEventModal] = useState(false);
  const [tapEventLoading, setTapEventLoading] = useState(false);
  const [tapEventError, setTapEventError] = useState('');
  const [tapEventResult, setTapEventResult] = useState(null);

  const TAP_EVENT_POPUP_CHANCE = 0.02; // 0.5% chance per tap when not on cooldown

  // Start cooldown until next UTC midnight (matches purchase_day_utc on the backend)
  const startBoosterCooldownForToday = () => {
    const now = new Date();
    const nextUtcMidnightMs = Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate() + 1,
      0,
      0,
      0,
      0,
    );
    const remainingMs = Math.max(0, nextUtcMidnightMs - now.getTime());
    if (remainingMs <= 0) return;

    setBoosterCooldownMs(remainingMs);
    if (hasLS() && user?.id) {
      try {
        localStorage.setItem(BOOSTER_COOLDOWN_KEY(user.id), String(nextUtcMidnightMs));
      } catch {}
    }
  };

  const activateBooster = (multiplier) => {
    const cost = getBoosterPrice(multiplier);
    if (!Number.isFinite(cost)) {
      setErrorMessage('Booster not available');
      return;
    }
    if ((user?.runes ?? 0) < cost) {
      setErrorMessage('Not Enough Runes');
      return;
    }
    setPendingBooster(multiplier);
    setShowConfirmModal(true);
  };

const guardedNavigate = async (page, params = {}) => {
  if (page !== 'home' && gated) {
    onWhitelistBlocked?.(page);
    return;
  }

  if (USE_BATCH_TAPS) {
    await flushTapQueue();
  }

  onNavigate?.(page, params);
};

  const openBooster = () => {
    if (blockIfGated('boosters')) return;
    setShowBoosterModal(true);
  };

  const confirmBoosterPurchase = async () => {
    if (!pendingBooster) return;
    const multiplier = pendingBooster;

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/activate-booster`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ multiplier }),
      });

      const data = await res.json();

      if (!res.ok) {
        // Backend enforces “one per day”: if we hit cooldown, also start local timer
        if (data?.error === 'Booster on cooldown') {
          startBoosterCooldownForToday();
        }
        setErrorMessage(data?.error || 'Failed to activate booster');
        return;
      }

      // update runes + booster locally
      setUser((prev) => ({ ...prev, runes: data.runes }));
      setBooster(multiplier);
      setBoosterTimeLeft(data.boosterTimeLeft || 60);

      // Successful purchase -> start cooldown until next UTC midnight
      startBoosterCooldownForToday();
    } catch (err) {
      console.error('Booster activation error:', err);
      setErrorMessage('Something went wrong. Please try again.');
    } finally {
      setShowConfirmModal(false);
      setPendingBooster(null);
    }
  };

  const batchSeqRef = useRef(0);

  const flushTapQueue = async () => {
    const q = pendingRef.current;
    if (flushingRef.current) return;
    if (q.taps <= 0) return;
    if (q.lockedDone) {
      q.taps = 0;
      q.inc = 0;
      bumpPending();
      return;
    }

    // Don’t start a second request if one is in-flight (keeps logic simple & stable)
    if (inflightRef.current.taps > 0) return;

    const seq = ++batchSeqRef.current;

    // snapshot what we're flushing
    const sendTaps = q.taps;
    const sendInc = q.inc;
    const sendBooster = q.booster;

    // ✅ Move pending -> inflight so UI does NOT jump
    inflightRef.current = {
      taps: sendTaps,
      inc: sendInc,
      booster: sendBooster,
      seq,
    };
    q.taps = 0;
    q.inc = 0;
    bumpPending();

    flushingRef.current = true;

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/tap/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          Accept: 'application/json',
        },
        body: JSON.stringify({ count: sendTaps, booster: sendBooster }),
        keepalive: true,
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        
         if (data?.error === 'HUMAN_CHECK_REQUIRED') {
          onNavigate?.('humancheck', {
            returnTo: 'tap',
          });
          return;
        }

        const infl = inflightRef.current;
        if (infl.seq === seq && !q.lockedDone) {
          // merge back
          if (q.taps === 0) q.booster = infl.booster;
          q.taps += infl.taps;
          q.inc += infl.inc;
          inflightRef.current = { taps: 0, inc: 0, booster: 1, seq: 0 };
          bumpPending();
        }
        return;
      }

      // ignore stale response
      if (seq !== batchSeqRef.current) return;

      // Apply server truth
      setUser((prev) => ({
        ...prev,
        xp: typeof data.updatedXp === 'number' ? data.updatedXp : prev.xp,
        sparks: typeof data.updatedSparks === 'number' ? data.updatedSparks : prev.sparks,
        credits: typeof data.credits === 'number' ? data.credits : prev.credits,
        runes: typeof data.runes === 'number' ? data.runes : prev.runes,
        level: typeof data.level === 'number' ? data.level : prev.level,
        levelTaps: typeof data.levelTaps === 'number' ? data.levelTaps : prev.levelTaps,
        tapDepletionCount:
          typeof data.tapDepletionCount === 'number'
            ? data.tapDepletionCount
            : prev.tapDepletionCount,
      }));

      // ✅ Clear inflight AFTER applying server truth
      if (inflightRef.current.seq === seq) {
        inflightRef.current = { taps: 0, inc: 0, booster: 1, seq: 0 };
        bumpPending();
      }

      if (data.leveledUp || data.completed) {
        setRechargeEndsAt(null);
        setWinPrize(Number(data.prizeAwarded) || 0);
        setWinItems(Array.isArray(data.awardedItems) ? data.awardedItems : []);
        setShowWinModal(true);
      }

      if (data.completed) {
        q.lockedDone = true;
        q.taps = 0;
        q.inc = 0;
        inflightRef.current = { taps: 0, inc: 0, booster: 1, seq: 0 };
        bumpPending();
      }
    } finally {
      flushingRef.current = false;
    }
  };

  useEffect(() => {
    if (!USE_BATCH_TAPS) return;

    flushTimerRef.current = setInterval(() => {
      flushTapQueue();
    }, BATCH_FLUSH_MS);

    return () => {
      if (flushTimerRef.current) clearInterval(flushTimerRef.current);
      flushTimerRef.current = null;

      // final flush on unmount (best effort)
      flushTapQueue();
    };
  }, []);

  useEffect(() => {
    if (!USE_BATCH_TAPS) return;

    const onVis = () => {
      if (document.visibilityState === 'hidden') flushTapQueue();
    };
    const onBeforeUnload = () => {
      // best effort (fetch may be canceled, but still try)
      flushTapQueue();
    };

    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('beforeunload', onBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, []);

  const handleTap = async (e) => {
    if (blockIfGated('tapping')) return;
    if (!canTap) return;

    const { clientX, clientY } = e;

    const id = Date.now() + Math.random();
    const driftX = (Math.random() - 0.5) * 24;
    const lifetime = 1500;

    const newEffect = {
      id,
      x: clientX,
      y: clientY,
      value: `+${booster} ${booster > 1 ? 'SPARKS' : 'SPARK'}`,
      driftX,
      lifetime,
    };

    // // // Randomly open tap event popup (no tap spent) if not on cooldown
    // if (!showTapEventModal && tapEventCooldownMs <= 0 && Math.random() < TAP_EVENT_POPUP_CHANCE) {
    //   playTapSfx();
    //   setTapEventError('');
    //   setTapEventResult(null);
    //   setShowTapEventModal(true);
    //   return;
    // }

    playTapSfx();

    if (USE_BATCH_TAPS) {
      // block if completed already
      if (pendingRef.current.lockedDone) return;

      // visuals
      setShowDamage(true);
      setTimeout(() => setShowDamage(false), 150);
      setTapEffects((prev) => [...prev, newEffect]);
      setTimeout(() => {
        setTapEffects((prev) => prev.filter((fx) => fx.id !== id));
      }, lifetime);

      const q = pendingRef.current;

      // If booster changes mid-pending, flush first (keeps math consistent)
      if (q.taps > 0 && q.booster !== booster) {
        flushTapQueue(); // don't await; keep UI instant
      }

      q.booster = booster;

      // Apply local optimistic delta to pending only
      q.taps += 1;
      q.inc += Math.max(1, booster);
      bumpPending();

      // Flush triggers:
      // 1) ran out of effective credits
      const afterCredits = Math.max(0, Number(user?.credits ?? 0) - q.taps);
      // <-- this is OK ONLY if user.credits is the server base
      // BUT: you must treat "0 reached" based on effectiveCredits *after this tap*:
      const effectiveAfter = Math.max(0, Number(user?.credits ?? 0) - q.taps);
      if (effectiveAfter <= 0) {
        flushTapQueue();
        return;
      }

      // 2) level reached locally (optional, but avoids “late” level completion)
      if (currentLevel.requiredTaps > 0) {
        const afterProgress = Number(user?.levelTaps ?? 0) + q.inc;
        if (afterProgress >= currentLevel.requiredTaps) {
          flushTapQueue();
          return;
        }
      }

      // 3) batch size
      if (q.taps >= BATCH_MAX_COUNT) flushTapQueue();

      return;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/tap`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          userId: user.id,
          booster,
          //freeTap: boosterActive,
        }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        if (errorData?.error === 'No credits left') {
          setUser((prev) => ({ ...prev, credits: 0 }));
          const serverNowMsRaw = getServerNowMs(res, errorData);
          const estFromCache = getEstimatedServerNowFromCache(user?.id);
          const estServerNow = serverNowMsRaw ?? estFromCache;
          const lastMs = errorData.lastRecharge ? Date.parse(errorData.lastRecharge) : null;
          if (estServerNow != null && lastMs != null) {
            const elapsed = Math.max(0, estServerNow - lastMs);
            const remaining = Math.max(0, CREDIT_RECHARGE_MS - elapsed);
            if (Number.isFinite(remaining) && remaining > 0) {
              startCountdown(estServerNow, remaining, { persist: true });
            }
          }
          return;
        }
        throw new Error('Server rejected the tap');
      }

      const data = await res.json();

      if (data.runesBonusAwarded && data.runesBonusAwarded > 0) {
        setRunesAwarded(data.runesBonusAwarded);
      }

      setShowDamage(true);
      setTimeout(() => setShowDamage(false), 150);
      setTapEffects((prev) => [...prev, newEffect]);
      setTimeout(() => {
        setTapEffects((prev) => prev.filter((fx) => fx.id !== id));
      }, lifetime);

      setUser((prevUser) => ({
        ...prevUser,
        xp: data.updatedXp,
        sparks: data.updatedSparks,
        credits: data.credits,
        runes: typeof data.runes === 'number' ? data.runes : prevUser.runes,
        level: typeof data.level === 'number' ? data.level : prevUser.level,
        levelTaps: typeof data.levelTaps === 'number' ? data.levelTaps : prevUser.levelTaps,
        tapDepletionCount:
          typeof data.tapDepletionCount === 'number'
            ? data.tapDepletionCount
            : prevUser.tapDepletionCount,
      }));

      if (data.leveledUp || data.completed) {
        setRechargeEndsAt(null);

        const prizeRunes = Number(data.prizeAwarded) || 0;

        // Prefer full objects from backend; fall back to empty list
        const items = Array.isArray(data.awardedItems) ? data.awardedItems : [];

        setWinPrize(prizeRunes);
        setWinItems(items);
        setShowWinModal(true);
      }
    } catch (err) {
      console.error('Tap rejected:', err);
    }
  };

  const formatTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
        .toString()
        .padStart(2, '0')}`;
    } else {
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
  };

  // --- assets per LEVEL frame (level 1..30 -> frame 0..9) ---
  const rawLevel = Number(user?.level) || 1;
  //const levelFrame = clamp(Math.floor((rawLevel - 1) / 3), 0, 9);
  const levelFrame = clamp(rawLevel - 1, 0, 29);

  const getTapImageSrc = ({ side = 'good' }) => {
  const sideKey = side === 'good' ? 'sun' : 'moon';
  return `/season2_${sideKey}_tap_${levelFrame}.png`;
};


  //const getBgImageSrc = ({ side = 'good' }) => {
  //  const sideKey = side === 'good' ? 'sun' : 'moon';
  //  return `/${sideKey}_bg_${levelFrame}.jpg`;
  //};

  const getBgImageSrc = ({ side = 'good' }) => {
    const sideKey = side === 'good' ? 'sun' : 'moon';
    return `/${sideKey}_tap_x.png`;
  };

  // Use level frame for sizing animations
  const c = levelFrame;

  const TAP_STYLE = {
    bad: {
     height: [
        '180%',
        '180%',
        '180%',
        '180%',
        '180%',
        '180%',
        '180%',
        '180%',
        '180%',
        '180%',
        '180%',
        '180%',
        '180%',
        '180%',
        '180%',
        '180%',
        '180%',
        '180%',
        '170%',
        '170%',
        '170%',
        '170%',
        '170%',
        '170%',
        '165%',
        '170%',
        '170%',
        '170%',
        '170%',
        '165%',
      ],
      margin: [
        '30px',
        '40px',
        '45px',
        '40px',
        '40px',
        '30px',
        '50px',
        '50px',
        '50px',
        '50px',
        '30px',
        '30px',
        '30px',
        '30px',
        '30px',
        '30px',
        '30px',
        '35px',
        '60px',
        '60px',
        '60px',
        '60px',
        '60px',
        '60px',
        '70px',
        '60px',
        '60px',
        '60px',
        '60px',
        '60px',
      ],
    },
    good: {
      height: [
        '180%',
        '180%',
        '180%',
        '180%',
        '180%',
        '180%',
        '180%',
        '180%',
        '180%',
        '180%',
        '180%',
        '180%',
        '180%',
        '180%',
        '180%',
        '180%',
        '180%',
        '180%',
        '180%',
        '180%',
        '175%',
        '170%',
        '170%',
        '170%',
        '170%',
        '170%',
        '170%',
        '170%',
        '170%',
        '165%',
      ],
      margin: [
        '20px',
        '20px',
        '20px',
        '20px',
        '40px',
        '50px',
        '50px',
        '50px',
        '40px',
        '40px',
        '30px',
        '30px',
        '30px',
        '30px',
        '30px',
        '30px',
        '30px',
        '35px',
        '40px',
        '40px',
        '50px',
        '50px',
        '55px',
        '55px',
        '55px',
        '55px',
        '60px',
        '60px',
        '60px',
        '55px',
      ],
    },
  };

  const pick = (arr, idx) => {
    const maxIndex = Math.max(0, arr.length - 1);
    const i = clamp(Number(idx || 0), 0, maxIndex);
    return arr[i];
  };

  const sideKeyChoice = user?.side === 'bad' ? 'bad' : 'good';

  const tapHeight = useMemo(() => pick(TAP_STYLE[sideKeyChoice].height, c), [c, sideKeyChoice]);
  const tapMargin = useMemo(() => pick(TAP_STYLE[sideKeyChoice].margin, c), [c, sideKeyChoice]);

  const numericCurrentLevel = Number(user?.level ?? 1) || 1;

  const currentGamepassRow = useMemo(() => {
    if (!gpLevels.length) return null;

    // Prefer explicit .level field, fall back to array index
    return (
      gpLevels.find((l) => Number(l.level ?? 0) === numericCurrentLevel) ||
      gpLevels[numericCurrentLevel - 1] ||
      null
    );
  }, [gpLevels, numericCurrentLevel]);

  const currentFreePrize = useMemo(() => {
    if (!currentGamepassRow) return null;
    const id = currentGamepassRow.prize_item_id || currentGamepassRow.prizeItemId;
    return id ? gpItemsById[id] || null : null;
  }, [currentGamepassRow, gpItemsById]);

  const currentOgPrize = useMemo(() => {
    if (!currentGamepassRow) return null;
    const id = currentGamepassRow.prize_gamepass_item_id || currentGamepassRow.prizeGamepassItemId;
    return id ? gpItemsById[id] || null : null;
  }, [currentGamepassRow, gpItemsById]);

  const currentPrize = useMemo(() => {
    // No prizes configured at all
    if (!currentGamepassRow) return null;

    // If the user has the gamepass, show ONLY the gamepass prize for this level
    if (hasGamepass) {
      return currentOgPrize || null;
    }

    // If the user does NOT have the gamepass, show ONLY the free prize for this level
    return currentFreePrize || null;
  }, [currentGamepassRow, currentFreePrize, currentOgPrize, hasGamepass]);

  const showPrizeLabel = !!currentPrize;

  // --- Progress bar value: levelTaps / requiredTaps ---
  const levelProgressPct = useMemo(() => {
    const progress = Number(effectiveLevelTaps);
    if (Number.isFinite(progress) && currentLevel.requiredTaps > 0) {
      return clamp((progress / currentLevel.requiredTaps) * 100, 0, 100);
    }

    // fallback: % of bar used
    const total = MAX_CREDITS || 1;
    const used = total - Number(effectiveCredits);
    return clamp((used / total) * 100, 0, 100);
  }, [effectiveLevelTaps, effectiveCredits, currentLevel.requiredTaps, MAX_CREDITS]);

  //const remainingMs = rechargeEndsAt != null ? Math.max(0, rechargeEndsAt - Date.now()) : null;

  const remainingMonotonic = useMemo(() => {
    if (initialRemainingMs == null || perfStart == null) return null;
    const elapsed = performance.now() - perfStart;
    return Math.max(0, initialRemainingMs - elapsed);
  }, [initialRemainingMs, perfStart, tick]);

  // B. Legacy fallback (UI only): absolute end time vs Date.now()
  const remainingLegacy = rechargeEndsAt != null ? Math.max(0, rechargeEndsAt - Date.now()) : null;

  // C. Final value used by the UI
  const remainingMs = remainingMonotonic ?? remainingLegacy ?? 0;
  const hasAnchor = endServerMsRef.current != null;

  useEffect(() => {
    if (!configReady) return;
    if (Number(user?.credits ?? 0) !== 0) return;
    if (!hasAnchor) return;
    if (remainingMs > 0) return;

    // countdown reached zero locally — check right now
    fetchRechargeStatus();
  }, [remainingMs, hasAnchor, configReady, user?.credits]);

  //useEffect(() => {
  //  if (Number(user?.credits ?? 0) === 0) {
  //    console.log('[recharge debug]', {
  //      rechargeEndsAt,
  //      initialRemainingMs,
  //      perfStartSet: !!perfStart,
  //   });
  // }
  //}, [user?.credits, rechargeEndsAt, initialRemainingMs, perfStart]);

  //const showCountdown = !boosterActive && hasAnchor && (remainingMs ?? 0) > 0;
  const showCountdown = hasAnchor && (remainingMs ?? 0) > 0;

  useEffect(() => {
    const onFocus = () => {
      if (Number(user?.credits ?? 0) === 0) fetchRechargeStatus();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [user?.credits]);

  const REFILL_VIDEO_TAPS = 50;
  const REFILL_STARS_COST = 50;

  const TAP_EVENT_COOLDOWN_MS = 3 * 60 * 60 * 1000; // 3 hours

  const formatTapEventCooldown = (ms) => {
    if (!ms || ms <= 0) return '';
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
        .toString()
        .padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleRefillVideo = async () => {
    setRefillError('');
    if (!tapRefillAdControllerRef.current) {
      setRefillError('Ad service not available');
      return;
    }
    setRefillLoading('video');
    try {
      await tapRefillAdControllerRef.current.show();
      // Ad was watched; Adsgram server will call our GET /refill-video-reward. Refresh credits.
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/status`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && typeof data.credits === 'number') {
        setUser((prev) => ({ ...prev, credits: data.credits }));
      }
      setShowRefillModal(false);
    } catch (error) {
      if (error?.error) {
        setRefillError('Ad failed to load – try again');
      } else {
        setRefillError('Ad was skipped or closed');
      }
    } finally {
      setRefillLoading(null);
    }
  };

  const handleRefillStars = async () => {
    setRefillError('');
    const isTMA = typeof window !== 'undefined' && !!window.Telegram?.WebApp?.openInvoice;
    if (!isTMA) {
      setRefillError('Refill with Stars is only available in the Telegram app.');
      return;
    }
    setRefillLoading('stars');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/refill-stars/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setRefillError(data?.error || 'Could not start payment');
        setRefillLoading(null);
        return;
      }
      const { orderId, invoiceLink } = data;
      window.Telegram.WebApp.openInvoice(invoiceLink, async (status) => {
        if (status !== 'paid') {
          setRefillError(status === 'cancelled' ? 'Payment cancelled' : 'Payment failed');
          setRefillLoading(null);
          return;
        }
        let tries = 0;
        const maxTries = 20;
        const wait = (ms) => new Promise((r) => setTimeout(r, ms));
        while (tries < maxTries) {
          const chk = await fetch(
            `${process.env.REACT_APP_API_URL}/api/telegram/stars/order/${orderId}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
              },
            },
          );
          if (chk.ok) {
            const j = await chk.json();
            if (j.status === 'paid') break;
          }
          tries++;
          await wait(1500);
        }
        const statusRes = await fetch(`${process.env.REACT_APP_API_URL}/api/status`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        const statusData = await statusRes.json().catch(() => ({}));
        if (statusRes.ok && typeof statusData.credits === 'number') {
          setUser((prev) => ({ ...prev, credits: statusData.credits }));
        }
        setRefillLoading(null);
        setShowRefillModal(false);
      });
    } catch (e) {
      setRefillError(e?.message || 'Failed to refill');
      setRefillLoading(null);
    }
  };

  const handleTapEventClaim = async () => {
    if (tapEventLoading) return;
    setTapEventError('');
    setTapEventLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/tap-event/claim`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          Accept: 'application/json',
        },
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        if (data?.error === 'TAP_EVENT_COOLDOWN') {
          const remaining = Number(data.cooldownMsRemaining) || TAP_EVENT_COOLDOWN_MS;
          setTapEventCooldownMs(remaining);
          if (hasLS() && user?.id) {
            try {
              const until = Date.now() + remaining;
              localStorage.setItem(TAP_EVENT_COOLDOWN_KEY(user.id), String(until));
            } catch {}
          }
          setTapEventError('Mystery event is resting right now. Try again a bit later.');
          return;
        }
        setTapEventError(data?.message || data?.error || 'Something went wrong. Please try again.');
        return;
      }

      // Apply currency + booster updates
      setUser((prev) => ({
        ...prev,
        sparks: typeof data.sparks === 'number' ? data.sparks : prev?.sparks ?? 0,
        runes: typeof data.runes === 'number' ? data.runes : prev?.runes ?? 0,
      }));

      if (data.boosterMultiplier && data.boosterMultiplier > 1) {
        setBooster(data.boosterMultiplier);
        setBoosterTimeLeft(Number(data.boosterTimeLeft) || 0);
      }

      const cooldownMs =
        Number(data.cooldownMs) && Number.isFinite(data.cooldownMs)
          ? Number(data.cooldownMs)
          : TAP_EVENT_COOLDOWN_MS;

      setTapEventCooldownMs(cooldownMs);
      if (hasLS() && user?.id) {
        try {
          const until = Date.now() + cooldownMs;
          localStorage.setItem(TAP_EVENT_COOLDOWN_KEY(user.id), String(until));
        } catch {}
      }

      setTapEventResult(data);
    } catch (err) {
      console.error('Tap event claim error:', err);
      setTapEventError('Something went wrong. Please try again.');
    } finally {
      setTapEventLoading(false);
    }
  };

  // Initialize Adsgram SDK for tap refill video (reward is granted via GET /api/refill-video-reward from Adsgram server)
  useEffect(() => {
    const blockId = process.env.REACT_APP_ADSGRAM_TAP_REFILL_BLOCK_ID || '22651';
    if (
      typeof window !== 'undefined' &&
      window.Adsgram &&
      blockId &&
      !tapRefillAdControllerRef.current
    ) {
      try {
        tapRefillAdControllerRef.current = window.Adsgram.init({
          blockId,
        });
      } catch (e) {
        console.warn('[Adsgram] Tap refill init failed:', e);
      }
    }
  }, []);

  const isReferred = !!(
    (
      user?.referred_by || // email / auth.js
      user?.referrer_id || // telegram / auth_tma.js
      user?.referrerId
    ) // camelCase fallback if /me maps it
  );

  const sparksAmount = isReferred ? newUsers?.bonusSparksFromReferrer : newUsers?.bonusSparks;

  const runesAmount = isReferred ? newUsers?.bonusRunesFromReferrer : newUsers?.bonusRunes;

  return (
    <div className="game-container">

      <div
      style={{
        width: "100%",
        maxWidth: 450,
        margin: "0 auto",
        marginTop: -18,
        paddingLeft: 0,
        paddingRight: 0,
        boxSizing: "border-box",
      }}
    >
      <GamesTopBar
  user={user}
  onNavigate={guardedNavigate}
  passType="gamepass"
  hasGamepass={hasGamepass}
/>
    </div>
      
<div className="inner">
       

        <div className="tap-wrapper">
          <div className="tap-circle" style={{ position: 'relative' }}>
            <motion.img
              src={getTapImageSrc({ side: user?.side ?? 'good' })}
              style={{
                height: tapHeight,
                marginTop: tapMargin,
                pointerEvents: canTap || gated || outOfTaps ? 'auto' : 'none',
                cursor: canTap || gated || outOfTaps ? 'pointer' : 'not-allowed',
                //filter: uiLocked || allLevelsDone || gated ? 'grayscale(90%)' : 'none',
                //opacity: uiLocked || allLevelsDone || gated ? 0.5 : 1,
                filter: uiLocked || allLevelsDone || gated || outOfTaps ? 'grayscale(100%)' : 'none',
                opacity: uiLocked || allLevelsDone || gated || outOfTaps ? 0.6 : 1,
              }}
              alt="Tap target"
              className="tap-target"
              whileTap={canTap ? { scale: 1.08 } : undefined}
              animate={uiLocked ? { y: 0 } : { y: [0, -5, 0] }}
              transition={{
                y: { duration: 2, repeat: Infinity, ease: 'easeInOut' }, // keyframes => tween
                scale: { type: 'spring', stiffness: 500, damping: 20 }, // tap => spring
              }}
              onClick={
                gated
                  ? () => onWhitelistBlocked?.('tapping')
                  : canTap
                  ? handleTap
                  : outOfTaps
                  ? () => {
                      if (ENABLE_REFILL_MODAL) {
                        setShowRefillModal(true);
                      }
                    }
                  : undefined
              }
              draggable={false}
              tabIndex={-1}
              onMouseDown={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
            />
          </div>
                </div>

        {/* LEVEL PROGRESS + PRIZE */}
        <div
          style={{
            width: "100%",
            height: 39,
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 7,
            padding: "4px 6px",
            boxSizing: "border-box",
            borderRadius: 7,
            background: "linear-gradient(180deg, #b86b3c 0%, #87401f 100%)",
            border: "2px solid #54230e",
            boxShadow:
              "inset 0 1px 0 rgba(255,255,255,0.28), 0 2px 0 rgba(61,24,8,0.35)",
          }}
        >
          {/* PROGRESS BAR */}
          <div
            style={{
              flex: "1 1 auto",
              minWidth: 0,
              height: 29,
              position: "relative",
              overflow: "hidden",
              borderRadius: 5,
              background: "#050505",
              border: "2px solid #2b0e05",
              boxSizing: "border-box",
            }}
          >
            {/* COLORED PROGRESS */}
            <div
              style={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: 0,
                width: `${levelProgressPct}%`,
                background:
                  "linear-gradient(180deg, #91f7e8 0%, #58d8ef 52%, #4587ee 100%)",
                boxShadow: "inset 0 2px 0 rgba(255,255,255,0.5)",
                transition: "width 0.2s ease",
              }}
            />

            {/* LEVEL INFO */}
<div
  className="shadow1"
  style={{
    position: "absolute",
    inset: 0,
    zIndex: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 5px",
    boxSizing: "border-box",
    color: "#fff",
    whiteSpace: "nowrap",
    pointerEvents: "none",
  }}
>
  <span
    style={{
      fontSize: 13,
      lineHeight: "25px",
      fontWeight: 800,
    }}
  >
    LEVEL {Number(user?.level ?? levelIndex + 1)}/{totalLevels}
  </span>

  <span
    style={{
      fontSize: 13,
      lineHeight: "25px",
      fontWeight: 800,
    }}
  >
    {Number(currentLevel.requiredTaps || 0).toLocaleString()} TAPS
  </span>
</div>
          </div>

          {/* CURRENT LEVEL PRIZE */}
          <div
            style={{
              flex: "0 0 auto",
              minWidth: 135,
              height: 29,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 4,
              whiteSpace: "nowrap",
              overflow: "hidden",
            }}
          >
            {showPrizeLabel && currentPrize ? (
              <>
                <img
                  src={getPrizeIconSrc(currentPrize)}
                  alt=""
                  draggable={false}
                  style={{
                    width: 23,
                    height: 23,
                    objectFit: "contain",
                    flexShrink: 0,
                    filter: "drop-shadow(0 2px 1px rgba(0,0,0,0.6))",
                  }}
                />

                <span
                  className="shadow1"
                  title={currentPrize.name || ""}
                  style={{
                    maxWidth: 135,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    color: "#fff",
                    fontSize: 16,
                    fontWeight: 600,
                  }}
                >
                  {currentPrize.name}
                </span>
              </>
            ) : (
              <span
                className="shadow1"
                style={{
                  color: "rgba(255,255,255,0.7)",
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                This Level Has No Prize
              </span>
            )}
          </div>
        </div>

        <div
          className="buttons-wrapper"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
            width: "100%",
            marginTop: 5,
          }}
        >
  <div
    style={{
      flex: "1 1 auto",
      minWidth: 0,
      height: 45,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
      padding: "0 10px",
      boxSizing: "border-box",
      borderRadius: 8,
      background: "#6f351d",
      border: "2px solid #3d1b0d",
      boxShadow:
        "inset 0 1px 0 rgba(255,255,255,0.12), 0 2px 0 rgba(74,28,10,0.45)",
    }}
  >
    <span
      className="shadow1"
      style={{
        color: "#fff",
        fontSize: 16,
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      Taps left:
    </span>

    <div
  style={{
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 4,
    minWidth: 0,
    whiteSpace: "nowrap",
  }}
>
  {allLevelsDone ? (
    <span
      style={{
        color: "#ffe21f",
        fontSize: 27,
        lineHeight: 1,
        fontWeight: 900,
        letterSpacing: "-1px",
        textShadow: `
          -2px -2px 0 #4b210b,
           2px -2px 0 #4b210b,
          -2px  2px 0 #4b210b,
           2px  2px 0 #4b210b,
           0 3px 0 #4b210b
        `,
      }}
    >
      0
    </span>
  ) : outOfTaps ? (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        justifyContent: "center",
        lineHeight: 1,
      }}
    >
      <span
        className="shadow1"
        style={{
          color: "#fff",
          fontSize: 10,
          fontWeight: 800,
          lineHeight: "11px",
        }}
      >
        RELOAD IN
      </span>

      <span
        style={{
          color: "#ffe21f",
          fontSize: 20,
          lineHeight: "22px",
          fontWeight: 900,
          letterSpacing: "-0.5px",
          textShadow: `
            -2px -2px 0 #4b210b,
             2px -2px 0 #4b210b,
            -2px  2px 0 #4b210b,
             2px  2px 0 #4b210b,
             0 3px 0 #4b210b
          `,
        }}
      >
        {showCountdown
          ? formatTime(remainingMs)
          : "RECHARGING"}
      </span>
    </div>
  ) : (
    <>
      <span
        style={{
          color: "#ffe21f",
          fontSize: 27,
          lineHeight: 1,
          fontWeight: 900,
          letterSpacing: "-1px",
          textShadow: `
            -2px -2px 0 #4b210b,
             2px -2px 0 #4b210b,
            -2px  2px 0 #4b210b,
             2px  2px 0 #4b210b,
             0 3px 0 #4b210b
          `,
        }}
      >
        {Math.max(
          0,
          Number(effectiveCredits ?? 0)
        )}
      </span>

      <span
        className="shadow1"
        style={{
          color: "#fff",
          fontSize: 16,
          fontWeight: 800,
        }}
      >
        / {Math.max(
          0,
          Number(MAX_CREDITS ?? 0)
        )}
      </span>
    </>
  )}
</div>
  </div>

  {boosterActive ? (
  <div
    style={{
      flex: "0 0 117px",
      width: 117,
      height: 45,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      boxSizing: "border-box",
      overflow: "hidden",
      borderRadius: 8,
      background:
        "linear-gradient(180deg, #2f9d54 0%, #176d39 100%)",
      border: "2px solid #104c28",
      boxShadow:
        "inset 0 1px 0 rgba(255,255,255,0.28), 0 2px 0 rgba(21,70,37,0.45)",
    }}
  >
    <div
      style={{
        flex: "1 1 auto",
        minWidth: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        paddingLeft: 6,
        paddingRight: 4,
        boxSizing: "border-box",
      }}
    >
      <div
        className="shadow1"
        style={{
          color: "#fff",
          fontSize: 12,
          lineHeight: "13px",
          fontWeight: 800,
          whiteSpace: "nowrap",
        }}
      >
        {booster}X BOOST
      </div>

      <div
        className="shadow1"
        style={{
          color: "#ffe94d",
          fontSize: 18,
          lineHeight: "19px",
          fontWeight: 900,
          whiteSpace: "nowrap",
        }}
      >
        {formatTime(boosterTimeLeft * 1000)}
      </div>
    </div>

    <div
      style={{
        flex: "0 0 34px",
        width: 34,
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.18)",
        borderLeft: "1px solid rgba(255,255,255,0.14)",
      }}
    >
      <img
        src="/energy.png"
        alt=""
        draggable={false}
        style={{
          width: 25,
          height: 25,
          objectFit: "contain",
          pointerEvents: "none",
          filter: "drop-shadow(0 2px 1px rgba(0,0,0,0.55))",
        }}
      />
    </div>
  </div>
) : (
  <img
    src="./v2/boosterbt.png"
    onClick={boosterButtonDisabled ? undefined : openBooster}
    style={{
      flex: "0 0 117px",
      width: 117,
      height: 45,
      display: "block",
      marginRight: 0,
      float: "right",
      opacity: boosterButtonDisabled ? 0.5 : 1,
      cursor: boosterButtonDisabled ? "not-allowed" : "pointer",
      pointerEvents: boosterButtonDisabled ? "none" : "auto",
      filter: uiLocked || allLevelsDone ? "grayscale(90%)" : "none",
    }}
    alt="Boosters"
  />
)}
</div>


      </div>

      {tapEffects.map((effect) => (
        <motion.div
          key={effect.id}
          initial={{ opacity: 0, y: 0, scale: 0.9 }}
          animate={{
            opacity: [0, 1, 1, 0],
            y: [0, -12, -80, -120],
            scale: [0.9, 1, 1, 1],
          }}
          transition={{
            duration: effect.lifetime / 1000,
            ease: 'easeOut',
            times: [0, 0.1, 0.85, 1],
          }}
          className="tap-effect"
          style={{
            top: effect.y - 30,
            left: effect.x - 30 + effect.driftX,
            position: 'fixed',
            pointerEvents: 'none',
            willChange: 'transform, opacity',
          }}
        >
          {effect.value}
        </motion.div>
      ))}

      {showConfirmModal && pendingBooster && (
        <div className="modal_box">

          <div className="modal_title">Confirm<button onClick={() => setShowConfirmModal(false)} className="modal_close"/></div>
          <motion.div className="modal_body" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}>

            <div className="modal_inside">

            <img src="/finger.png" alt="Success" style={{ height: '80px', marginBottom: '5px' }} />
            <div className="shadow2" style={{ marginBottom: 10, fontWeight: 600, fontSize: 28}}>Confirm Purchase?</div>
            <div
            className="info"
              
            >
              Spend{' '}
              <span style={{ color: '#ffbb01' }}>{getBoosterPrice(pendingBooster)} Runes</span> to
              activate <span style={{ color: '#09BDFF' }}>{pendingBooster}x booster</span>?
            </div>
            </div>
            <div className="modal_footer">
              <button className="game-button green-button" style={{marginRight: 10}} onClick={confirmBoosterPurchase}>Confirm</button>
              <button className="game-button red-button"  onClick={() => setShowConfirmModal(false)}>CANCEL</button>
            </div>
          </motion.div>
        </div>
      )}

      {showOpenBonusModal && (
        <OpenBonusModal
          onClose={() => setShowOpenBonusModal(false)}
          user={user}
          setUser={setUser}
        />
      )}

      {ENABLE_REFILL_MODAL && showRefillModal && outOfTaps && (
        <div className="modal-backdrop">
          <motion.div
            className="modal fancy-success-modal"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            <img src="/energy.png" alt="Out of energy" style={{ height: 72, marginBottom: 6 }} />
            <h2
              style={{
                color: '#FFD54F',
                marginBottom: 8,
                textShadow: '1px 1px #000',
              }}
            >
              Out of Taps
            </h2>
            <p
              style={{
                fontSize: 18,
                color: '#ddd',
                textShadow: '1px 1px #000',
                margin: '0 0 12px',
              }}
            >
              Choose how you want to refill and keep fighting.
            </p>
            <div
              style={{
                display: 'grid',
                gap: 8,
                margin: '8px auto 4px',
                width: '100%',
              }}
            >
              <button
                type="button"
                disabled={!!refillLoading}
                onClick={handleRefillVideo}
                style={{
                  padding: '10px 12px',
                  borderRadius: 10,
                  border: 'none',
                  background: 'linear-gradient(90deg, #4FC3F7, #29B6F6, #0288D1)',
                  color: '#fff',
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: refillLoading ? 'not-allowed' : 'pointer',
                  boxShadow: '0 4px 10px rgba(0,0,0,0.4)',
                }}
              >
                {refillLoading === 'video'
                  ? 'Loading video...'
                  : `Watch video (+${REFILL_VIDEO_TAPS} taps)`}
              </button>
              <button
                type="button"
                disabled={
                  !!refillLoading ||
                  !(typeof window !== 'undefined' && window.Telegram?.WebApp?.openInvoice)
                }
                onClick={handleRefillStars}
                style={{
                  padding: '10px 12px',
                  borderRadius: 10,
                  border: 'none',
                  background: 'linear-gradient(90deg, #FFB300, #FF8F00, #F57C00)',
                  color:
                    !!refillLoading ||
                    !(typeof window !== 'undefined' && window.Telegram?.WebApp?.openInvoice)
                      ? '#555'
                      : '#fff',
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: refillLoading ? 'not-allowed' : 'pointer',
                  boxShadow: '0 4px 10px rgba(0,0,0,0.4)',
                }}
              >
                {refillLoading === 'stars'
                  ? 'Starting payment...'
                  : `Refill for ${REFILL_STARS_COST} Stars`}
              </button>
            </div>
            {refillError && (
              <p
                style={{
                  marginTop: 8,
                  fontSize: 13,
                  color: '#ff6b6b',
                }}
              >
                {refillError}
              </p>
            )}
            <div className="modal-footer" style={{ marginTop: 10 }}>
              <button
                onClick={() => {
                  if (refillLoading) return;
                  setRefillError('');
                  setShowRefillModal(false);
                }}
              >
                Maybe later
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {showTapEventModal && (
        <div className="modal-backdrop">
          <motion.div
            className="modal fancy-success-modal"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            <img src="/chest.png" alt="Mystery reward" style={{ height: 80, marginBottom: 6 }} />
            <h2
              style={{
                color: '#FFD54F',
                marginBottom: 8,
                textShadow: '1px 1px #000',
              }}
            >
              Lucky Tap Event
            </h2>
            <p
              style={{
                fontSize: 16,
                color: '#ddd',
                textShadow: '1px 1px #000',
                margin: '0 0 8px',
              }}
            >
              Random reward when you tap:
            </p>
            <div
              style={{
                display: 'grid',
                gap: 6,
                margin: '6px auto 10px',
                padding: '8px 12px',
                background: 'rgba(0,0,0,0.4)',
                borderRadius: 12,
                maxWidth: 320,
                textAlign: 'left',
                fontSize: 14,
              }}
            >
              <div style={{ color: '#eee' }}>• 50% – Get 10 Sparks</div>
              <div style={{ color: '#eee' }}>• 20% – Get 2x booster for 10s</div>
              <div style={{ color: '#eee' }}>• 20% – Get 1 Rune</div>
              <div style={{ color: '#eee' }}>• 10% – Get 2x booster for 20s</div>
            </div>

            {tapEventResult && (
              <div
                style={{
                  marginBottom: 8,
                  color: '#B2FF59',
                  textShadow: '1px 1px #000',
                  fontSize: 16,
                }}
              >
                {tapEventResult.kind === 'video_sparks' && '+10 Sparks added to your balance!'}
                {tapEventResult.kind === 'booster_10s' &&
                  '2x booster activated for 10s! Tap fast for a burst of power!'}
                {tapEventResult.kind === 'video_rune' && '+1 Rune added to your balance!'}
                {tapEventResult.kind === 'booster_20s' &&
                  `2x booster activated for 20s! Tap fast for a burst of power!`}
              </div>
            )}

            {tapEventCooldownMs > 0 && (
              <p
                style={{
                  fontSize: 13,
                  color: '#ccc',
                  textShadow: '1px 1px #000',
                  margin: '4px 0',
                }}
              >
                Next mystery event in {formatTapEventCooldown(tapEventCooldownMs)}.
              </p>
            )}

            {tapEventError && (
              <p
                style={{
                  marginTop: 4,
                  fontSize: 13,
                  color: '#ff6b6b',
                }}
              >
                {tapEventError}
              </p>
            )}

            <div className="modal-footer" style={{ marginTop: 10 }}>
              {!tapEventResult && (
                <button
                  onClick={handleTapEventClaim}
                  disabled={tapEventLoading}
                  style={{ marginRight: 8 }}
                >
                  {tapEventLoading ? 'Rolling...' : 'Claim reward'}
                </button>
              )}
              <button
                onClick={() => {
                  if (tapEventLoading) return;
                  setTapEventError('');
                  setTapEventResult(null);
                  setShowTapEventModal(false);
                }}
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {errorMessage && (
        <div className="modal-backdrop">
          <motion.div
            className="modal fancy-success-modal"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <img
              src="/finger.png"
              alt="Error"
              style={{
                height: '80px',
                marginBottom: '10px',
                transform: 'rotate(180deg)',
              }}
            />
            <h2 style={{ color: '#ff4d4d', marginBottom: '10px' }}>Transaction Failed</h2>
            <p
              style={{
                textTransform: 'capitalize',
                textShadow: '1px 1px #000',
                fontSize: '24px',
                color: '#ddd',
              }}
            >
              {errorMessage}
            </p>
            <div className="modal-footer">
              <button className="buy-runes-btn" onClick={() => onOpenShop?.('coin_2')}>
                Buy Runes
              </button>
              <br />
              <button
                onClick={() => {
                  setErrorMessage('');
                }}
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Completion prize modal */}
      {showWinModal && (
        <div className="modal_box">
          <motion. div
            className="modal_box" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
          >

          <div className="modal_title">Level Complete<button className="modal_close" onClick={() => setShowWinModal(false)}/></div>
          <div className="modal_body">
            <div className="modal_inside">
            <img src="/won.png" alt="Winner" style={{ height: 80 }} />
            <div
            className="shadow2"
              style={{
                fontWeight: 600,
                fontSize: 28
              }}
            >
              Level Complete
            </div>

            {(winPrize > 0 || winItems.length > 0) && (
              <div
              className="shadow1"
                style={{
                  marginTop: 10,
                  marginBottom: 10,
                  fontWeight: 600,
                  fontSize: 21
                }}
              >
                {winItemsSummary}
              </div>
            )}

            {(winPrize > 0 || winItems.length > 0) && (
              <div
              className="info"
                style={{
                  display: 'grid',
                  gap: 10,
                  textAlign: 'left',
                }}
              >
                {/* Completion runes prize, if any */}
                {winPrize > 0 && (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '28px 1fr auto',
                      alignItems: 'center',
                      gap: 10,
                    }}
                  >
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                      }}
                    >
                      <img
                        src="/runes.png"
                        alt="Runes"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                        }}
                      />
                    </div>

                    <div style={{ color: '#eee' }}>Completion Prize</div>

                    <div
                    className="shadow1"
                      style={{
                        color: '#fff',
                        background: 'linear-gradient(90deg,#FFD54F,#FF6EC7)',
                        padding: '2px 10px',
                        borderRadius: 999,
                        fontWeight: 600,
                      }}
                    >
                      +{winPrize}
                    </div>
                  </div>
                )}

                {/* Level-configured prize items */}
                {winItems.map((item) => (
                  <div
                    key={item.id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '28px 1fr auto',
                      alignItems: 'center',
                      gap: 10,
                    }}
                  >
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                      }}
                    >
                      <img
                        src={getPrizeIconSrc(item)}
                        alt={item.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                        }}
                      />
                    </div>

                    <div style={{ color: '#eee', fontSize: '18px' }}>
                      {item.name}

                      <div
                        style={{
                          color: isDirectBalanceReward(item) ? '#FFD54F' : '#7ED957',
                          fontSize: 14,
                          marginTop: 2,
                        }}
                      >
                        {isDirectBalanceReward(item)
                          ? 'Credited to your balance'
                          : 'Added to your inventory'}
                      </div>

                      {item.isFinalFragment ? (
                        <div
                          style={{
                            color: '#7ED957',
                            fontSize: 18,
                            marginTop: 2,
                          }}
                        >
                          🎉 Last fragment collected
                        </div>
                      ) : null}
                    </div>

                    <div
                    className="shadow1"
                      style={{
                        color: '#fff',
                        background: 'linear-gradient(90deg,#FFD54F,#FF6EC7)',
                        padding: '2px 10px',
                        borderRadius: 999,
                        fontWeight: 600,
                      }}
                    >
                      +1
                    </div>
                  </div>
                ))}
              </div>
            )}

            {winPrize === 0 && winItems.length === 0 && (
              <div className="info"
              >
                You Totally Crushed It!
              </div>
            )}

            </div>

            <div className="modal_footer">
              <button
                className="game-button green-button" style={{marginRight: 10}}
                onClick={() => {
                  setShowWinModal(false);
                  onNavigate('inventoryHub'); // or guardedNavigate("inventory");
                }}
              >
                Inventory
              </button>
              <button className="game-button red-button" onClick={() => setShowWinModal(false)}>CLOSE</button>
            </div>
          </div></motion.div>
        </div>
      )}

      {showBoosterModal && (
        <div
          className="modal_box"
        >

        <div className="modal_title">Boosters<button className="modal_close" onClick={() => setShowBoosterModal(false)} /></div>
          <motion.div className="modal_body" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}>
            
          <div className="modal_inside">
            <img src="./rocket.png" style={{ height: '70px' }} />

            <div className="shadow2" style={{ fontWeight: 600, fontSize: 28}}>
              Tap Boosters
            </div>

            {boosterOnCooldown && (
              <p
              className="shadow1"
                style={{
                  marginTop: 10,
                  marginBottom: 10,
                  fontSize: 21,
                  fontWeight: 600,
                }}
              >
                Next booster in {formatTime(boosterCooldownMs)}
              </p>
            )}

            <div
              className="booster-selection"
              style={{
                marginTop: 10,
                marginBottom: 0,
                flexDirection: 'column',
                textAlign: 'center',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {boosterTiers.map(({ increment: multiplier, price: cost }) => {
                return (
                  <div
                    key={multiplier}
                    className="info task-card"
                    onClick={() => {
                      if (boosterOnCooldown) return;
                      activateBooster(multiplier);
                      setShowBoosterModal(false);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                      }}
                    >
                      <div
                        className="amount"
                        style={{
                          fontSize: '16px',
                          backgroundImage: "url('/energy.png')",
                          backgroundSize: '24px',
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'left center',
                          paddingLeft: '30px',
                          color: '#ffcc62',
                          textShadow: '1px 1px #000',
                          lineHeight: "24px"
                        }}
                      >
                        {multiplier}X For 60 Seconds
                      </div>
                    </div>
                    <div
                      className="amount"
                      style={{
                        paddingRight: '0px',
                        fontSize: '16px',
                        backgroundImage: "url('/runes.png')",
                        backgroundSize: '19px',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'left center',
                        paddingLeft: '24px',
                        color: '#fff',
                        textShadow: '1px 1px #000',
                      }}
                    >
                      {cost} Runes
                    </div>
                  </div>
                );
              })}
            </div>
            </div>

            <div className="modal_footer">
              <button className="game-button red-button" onClick={() => setShowBoosterModal(false)}>CLOSE</button>
            </div>
          </motion.div>
        </div>
      )}

     {uiLocked && (
  <div
    style={{
      position: 'fixed',
      inset: 0,
      zIndex: 10000,
      background: 'rgba(0,0,0,0.35)',
      backdropFilter: 'blur(2px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      pointerEvents: 'auto',
    }}
  >
    <LoadingSpinner text="Loading Tap Game" size={64} />
  </div>
)}
    </div>
  );
};

export default TapGame;
